import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import CommonWrapper from '../../components/ui_components/CommonComponentsStructureCss';
import colors from '../../styles/colors';
import { apiGetQuizLeaderboard } from '../../api/quizApi';
import type { QuizLeaderboardEntry } from '../../types/models/quiz';

type Period = 'all' | 'week' | 'month';

const PERIOD_LABELS: Record<Period, string> = { all: 'Sempre', week: 'Settimana', month: 'Mese' };

const RANK_COLORS: Record<number, string> = { 1: 'gold', 2: '#c0c0c0', 3: '#cd7f32' };

// ─── Styled ───────────────────────────────────────────────────────────────────

const PageInner = styled.div`
  max-width: 860px;
  margin: 0 auto;
  padding: 48px 24px 80px;
`;

const PageTitle = styled.h1`
  font-size: 26px;
  font-weight: 900;
  color: #fff;
  margin: 0 0 4px;
`;

const PageSub = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.4);
  margin: 0 0 28px;
`;

const PeriodRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
`;

const PeriodBtn = styled.button<{ $active: boolean }>`
  padding: 6px 18px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${({ $active }) => ($active ? '#38bdf8' : 'rgba(255,255,255,0.12)')};
  background-color: ${({ $active }) => ($active ? 'rgba(56,189,248,0.12)' : 'transparent')};
  color: ${({ $active }) => ($active ? '#38bdf8' : 'rgba(255,255,255,0.45)')};
  transition: all 0.15s;
  &:hover { border-color: #38bdf8; color: #38bdf8; }
`;

const Table = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: 48px 1fr 80px 80px 80px;
  padding: 6px 16px;
  gap: 8px;
`;

const HeaderCell = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.3);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  text-align: right;
  &:first-child { text-align: left; }
  &:nth-child(2) { text-align: left; }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 48px 1fr 80px 80px 80px;
  align-items: center;
  padding: 12px 16px;
  gap: 8px;
  background-color: ${colors.bgColorBlack2};
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s;
  &:hover {
    border-color: rgba(56, 189, 248, 0.3);
    background-color: rgba(56, 189, 248, 0.04);
  }
`;

const RankNum = styled.span<{ $rank: number }>`
  font-size: 15px;
  font-weight: 900;
  color: ${({ $rank }) => RANK_COLORS[$rank] ?? 'rgba(255,255,255,0.3)'};
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

const Username = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Nickname = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.35);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Cell = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.8);
  text-align: right;
`;

const PctCell = styled(Cell)<{ $value: number }>`
  color: ${({ $value }) => ($value >= 70 ? '#5adf7a' : $value >= 40 ? '#fbbf24' : 'rgba(255,255,255,0.5)')};
`;

const StatusText = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.35);
  margin-top: 48px;
  font-size: 14px;
`;

// ─── Component ────────────────────────────────────────────────────────────────

const QuizLeaderboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>('all');
  const [entries, setEntries] = useState<QuizLeaderboardEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    apiGetQuizLeaderboard(period)
      .then(setEntries)
      .catch(() => setEntries([]))
      .finally(() => setIsLoading(false));
  }, [period]);

  return (
    <CommonWrapper>
      <PageInner>
        <PageTitle>Classifica Quiz</PageTitle>
        <PageSub>Chi risponde meglio alle domande GWF</PageSub>

        <PeriodRow>
          {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
            <PeriodBtn key={p} $active={period === p} onClick={() => setPeriod(p)}>
              {PERIOD_LABELS[p]}
            </PeriodBtn>
          ))}
        </PeriodRow>

        {isLoading && <StatusText>Caricamento...</StatusText>}

        {!isLoading && entries.length === 0 && (
          <StatusText>Nessun dato disponibile per questo periodo.</StatusText>
        )}

        {!isLoading && entries.length > 0 && (
          <Table>
            <HeaderRow>
              <HeaderCell>#</HeaderCell>
              <HeaderCell>Utente</HeaderCell>
              <HeaderCell>Corrette</HeaderCell>
              <HeaderCell>Totali</HeaderCell>
              <HeaderCell>%</HeaderCell>
            </HeaderRow>
            {entries.map((e, i) => (
              <Row key={e.user_id} onClick={() => navigate(`/carte/utente/${e.username}`)}>
                <RankNum $rank={i + 1}>{i + 1}</RankNum>
                <UserInfo>
                  <Username>{e.username}</Username>
                  {e.nickname_forum && <Nickname>{e.nickname_forum}</Nickname>}
                </UserInfo>
                <Cell>{e.correct_answers}</Cell>
                <Cell>{e.total_answers}</Cell>
                <PctCell $value={e.percentage}>{e.percentage.toFixed(1)}%</PctCell>
              </Row>
            ))}
          </Table>
        )}
      </PageInner>
    </CommonWrapper>
  );
};

export default QuizLeaderboardPage;
